/**
 * ImportPage — Page centralisée d'import de données Olympia
 *
 * 2 onglets :
 * 1. Données astrologiques — CSV (Natal, RS, Progressions) + PDF (Transits)
 * 2. Autres documents — PDF visuels (Roue, Tableaux, Listing, Dignités)
 *
 * Pour chaque type, des instructions précises expliquent
 * quelles dates sélectionner et comment exporter depuis Olympia.
 */

import { useState, useRef, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { apiFetch, apiUpload } from '../utils/api.js';
import { colors } from '../styles/tokens.js';
import { useClient } from '../contexts/ClientContext.js';

// ─── Types ───

type ImportTab = 'data' | 'documents';
type DataType = 'natal' | 'revolution_solaire' | 'progressions' | 'transits';

const DOC_TYPES = [
  { key: 'theme_natal_roue', label: 'Thème Natal (Roue)', icon: 'bi-pie-chart', color: '#2563eb' },
  { key: 'tableau_planetes', label: 'Tableau Planètes', icon: 'bi-table', color: '#16a34a' },
  { key: 'tableau_aspects', label: 'Tableau Aspects', icon: 'bi-diagram-3', color: '#9333ea' },
  { key: 'listing_aspects', label: 'Listing Aspects', icon: 'bi-list-ul', color: '#dc2626' },
  { key: 'tableau_dignites', label: 'Tableau Dignités', icon: 'bi-award', color: '#ca8a04' },
  { key: 'autre', label: 'Autre document', icon: 'bi-file-pdf', color: '#666' },
] as const;

interface TransitResult {
  chart: { id: number };
  metadata: {
    dateDebut: string | null;
    dateFin: string | null;
    nomClient: string | null;
    totalPages: number;
  };
  stats: {
    total: number;
    parPlanete: Record<string, number>;
    parAspect: Record<string, number>;
  };
  symbolesInconnus: Array<{ type: string; symbole: string }>;
  totalAspects: number;
}

// ─── Composant principal ───

export function ImportPage() {
  const { id } = useParams<{ id: string }>();

  const [activeTab, setActiveTab] = useState<ImportTab>('data');

  const tabs: { key: ImportTab; label: string; icon: string; color: string }[] = [
    { key: 'data', label: 'Données astrologiques', icon: 'bi-stars', color: '#2563eb' },
    { key: 'documents', label: 'Autres documents', icon: 'bi-file-earmark-pdf', color: '#dc2626' },
  ];

  return (
    <div style={{ maxWidth: 1000, width: '100%' }}>
      <h1 style={{ margin: '0 0 1.5rem', fontSize: '1.4rem', fontWeight: 700, color: colors.text }}>
        <i className="bi bi-cloud-arrow-up" style={{ color: colors.accent, marginRight: 10 }} />
        Importer des données
      </h1>

      {/* Onglets */}
      <div style={{
        display: 'flex', gap: 4, marginBottom: '1.5rem',
        background: colors.bgPage, borderRadius: 10, padding: 4,
      }}>
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            style={{
              flex: 1, padding: '10px 16px', borderRadius: 8, border: 'none',
              background: activeTab === tab.key ? '#fff' : 'transparent',
              boxShadow: activeTab === tab.key ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
              color: activeTab === tab.key ? colors.text : colors.textMuted,
              fontWeight: activeTab === tab.key ? 600 : 400,
              fontSize: '0.9rem', cursor: 'pointer', fontFamily: 'inherit',
              transition: 'all 0.2s',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            }}
          >
            <i className={`bi ${tab.icon}`} style={{ color: activeTab === tab.key ? tab.color : colors.textMuted }} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Contenu des onglets */}
      {activeTab === 'data' && <DataImportSection clientId={id!} />}
      {activeTab === 'documents' && <DocumentImportSection clientId={id!} />}
    </div>
  );
}

// ─── Section Données astrologiques (CSV + Transits PDF) ───

function DataImportSection({ clientId }: { clientId: string }) {
  const { client, reloadClient } = useClient();
  const [dataType, setDataType] = useState<DataType>('natal');
  const [annee, setAnnee] = useState(new Date().getFullYear());
  const [progDate, setProgDate] = useState(''); // Date complète pour les progressions (YYYY-MM-DD)
  const [transitDateDebut, setTransitDateDebut] = useState(''); // Date début transits
  const [transitDateFin, setTransitDateFin] = useState(''); // Date fin transits

  // Vérifier si une RS ou Progressions existe déjà pour cette année
  const existingForYear = client?.charts.find((c) => {
    if (dataType === 'revolution_solaire' && c.type === 'REVOLUTION_SOLAIRE' && c.anneeRs === annee) return true;
    if (dataType === 'progressions' && c.type === 'PROGRESSIONS' && c.anneeRs === annee) return true;
    return false;
  });

  // CSV state
  const [csvContent, setCsvContent] = useState('');
  const [csvFileName, setCsvFileName] = useState('');
  const [csvDragging, setCsvDragging] = useState(false);
  const csvInputRef = useRef<HTMLInputElement>(null);

  // Transit PDF state
  const [transitUploading, setTransitUploading] = useState(false);
  const [transitProgress, setTransitProgress] = useState('');
  const [transitDragOver, setTransitDragOver] = useState(false);
  const [transitResult, setTransitResult] = useState<TransitResult | null>(null);
  const transitInputRef = useRef<HTMLInputElement>(null);

  // Shared
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const isTransit = dataType === 'transits';
  const needsYear = dataType === 'revolution_solaire' || dataType === 'progressions';

  /** Détecte le type depuis le contenu du CSV */
  function detectTypeFromCSV(content: string) {
    const lines = content.split(/\r?\n/).map((l) => l.trim()).filter((l) => l.length > 0);
    if (lines.length === 0) return;
    const firstLine = lines[0]!.toLowerCase();

    // Chercher une date dans la première ligne (DD/MM/YYYY)
    const dateMatch = lines[0]!.match(/(\d{1,2})\/(\d{1,2})\/(\d{4})/);
    const detectedYear = dateMatch?.[3] ? parseInt(dateMatch[3], 10) : null;

    if (firstLine.includes('révolution solaire') || firstLine.includes('revolution solaire')) {
      setDataType('revolution_solaire');
      if (detectedYear) {
        setAnnee(detectedYear);
        setMessage({ type: 'success', text: `Révolution Solaire ${detectedYear} détectée` });
      } else {
        setMessage({ type: 'error', text: 'Révolution Solaire détectée — vérifiez l\'année avant d\'importer' });
      }
    } else if (firstLine.includes('directions')) {
      setDataType('progressions');
      if (detectedYear) {
        setAnnee(detectedYear);
        setMessage({ type: 'success', text: `Progressions ${detectedYear} détectées` });
      } else {
        setMessage({ type: 'error', text: 'Progressions détectées — vérifiez l\'année et la date avant d\'importer' });
      }
    } else if (firstLine.includes('thème natal') || firstLine.includes('theme natal')) {
      setDataType('natal');
      setMessage({ type: 'success', text: 'Thème Natal détecté automatiquement' });
    }
  }

  const readCsvFile = useCallback((file: File) => {
    if (!file.name.toLowerCase().endsWith('.csv') && !file.type.includes('csv') && !file.type.includes('text')) {
      setMessage({ type: 'error', text: 'Veuillez sélectionner un fichier CSV' });
      return;
    }
    setCsvFileName(file.name);
    setMessage(null);
    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target?.result as string ?? '';
      setCsvContent(content);
      detectTypeFromCSV(content);
    };
    reader.readAsText(file, 'UTF-8');
  }, []);

  function fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const res = reader.result as string;
        resolve(res.split(',')[1] ?? res);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  async function handleTransitFile(file: File) {
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      setMessage({ type: 'error', text: 'Seuls les fichiers PDF sont acceptés pour les transits' });
      return;
    }
    if (file.size > 100 * 1024 * 1024) {
      setMessage({ type: 'error', text: 'Fichier trop volumineux (max 100 MB)' });
      return;
    }
    setTransitUploading(true);
    setMessage(null);
    setTransitProgress('Lecture du fichier...');
    try {
      const base64 = await fileToBase64(file);
      setTransitProgress('Envoi au serveur et parsing du PDF... (peut prendre quelques minutes)');
      const res = await apiFetch<TransitResult>(`/clients/${clientId}/charts/import-transit-pdf`, {
        method: 'POST',
        body: JSON.stringify({
          pdfBase64: base64,
          ...(transitDateDebut && { dateDebut: transitDateDebut }),
          ...(transitDateFin && { dateFin: transitDateFin }),
        }),
        timeout: 600000,
      });
      if (res.success && res.data) {
        setTransitResult(res.data);
        setTransitProgress('');
        await reloadClient();
      } else {
        setMessage({ type: 'error', text: res.error ?? "Erreur lors de l'import" });
      }
    } catch (err) {
      setMessage({ type: 'error', text: err instanceof Error ? err.message : 'Erreur de connexion' });
    } finally {
      setTransitUploading(false);
    }
  }

  async function handleCsvSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!csvContent.trim()) return;
    setLoading(true);
    setMessage(null);
    try {
      const body: Record<string, unknown> = { csv: csvContent };
      if (dataType === 'revolution_solaire') {
        body.type = 'revolution_solaire';
        body.anneeRs = annee;
      } else if (dataType === 'progressions') {
        body.type = 'progressions';
        body.anneeRs = annee;
      }
      const res = await apiFetch<{ chart: { id: number } }>(`/clients/${clientId}/charts/import-csv`, {
        method: 'POST',
        body: JSON.stringify(body),
      });
      if (res.success) {
        setMessage({ type: 'success', text: 'Import réussi !' });
        setCsvContent('');
        setCsvFileName('');
        await reloadClient();
      } else {
        setMessage({ type: 'error', text: res.error ?? "Erreur lors de l'import" });
      }
    } catch {
      setMessage({ type: 'error', text: 'Erreur de connexion au serveur' });
    } finally {
      setLoading(false);
    }
  }

  const typeCards: { key: DataType; label: string; icon: string; color: string; desc: string; format: string }[] = [
    { key: 'natal', label: 'Thème Natal', icon: 'bi-stars', color: '#2563eb', desc: 'Carte du ciel de naissance', format: 'CSV' },
    { key: 'revolution_solaire', label: 'Révolution Solaire', icon: 'bi-sun', color: '#16a34a', desc: 'Thème annuel', format: 'CSV' },
    { key: 'progressions', label: 'Progressions', icon: 'bi-clock-history', color: '#7c3aed', desc: 'Directions secondaires', format: 'CSV' },
    { key: 'transits', label: 'Transits', icon: 'bi-arrow-repeat', color: '#0891b2', desc: 'Traqueur de Dates', format: 'PDF' },
  ];

  // Affichage résultat transits
  if (transitResult) {
    return (
      <div style={cardStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: '1.5rem', color: '#166534', fontWeight: 600 }}>
          <i className="bi bi-check-circle-fill" />
          Import réussi — {transitResult.totalAspects} aspects extraits
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.5rem' }}>
          <div style={{ background: colors.bgPage, borderRadius: 6, padding: '0.75rem 1rem' }}>
            <div style={{ fontSize: '0.75rem', color: colors.textMuted, marginBottom: 4 }}>Période</div>
            <div style={{ fontSize: '0.9rem', fontWeight: 500 }}>
              {transitResult.metadata.dateDebut ?? '?'} → {transitResult.metadata.dateFin ?? '?'}
            </div>
          </div>
          <div style={{ background: colors.bgPage, borderRadius: 6, padding: '0.75rem 1rem' }}>
            <div style={{ fontSize: '0.75rem', color: colors.textMuted, marginBottom: 4 }}>Pages analysées</div>
            <div style={{ fontSize: '0.9rem', fontWeight: 500 }}>{transitResult.metadata.totalPages} pages</div>
          </div>
        </div>
        <h3 style={{ fontSize: '0.9rem', fontWeight: 600, marginBottom: '0.75rem' }}>Aspects par planète transitante</h3>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '1.5rem' }}>
          {Object.entries(transitResult.stats.parPlanete).sort((a, b) => b[1] - a[1]).map(([p, c]) => (
            <span key={p} style={{ background: colors.bgPage, borderRadius: 20, padding: '4px 12px', fontSize: '0.8rem', fontWeight: 500 }}>{p}: {c}</span>
          ))}
        </div>
        <h3 style={{ fontSize: '0.9rem', fontWeight: 600, marginBottom: '0.75rem' }}>Par type d'aspect</h3>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '1.5rem' }}>
          {Object.entries(transitResult.stats.parAspect).sort((a, b) => b[1] - a[1]).map(([a, c]) => (
            <span key={a} style={{ background: colors.bgPage, borderRadius: 20, padding: '4px 12px', fontSize: '0.8rem', fontWeight: 500 }}>{a}: {c}</span>
          ))}
        </div>
        {transitResult.symbolesInconnus.length > 0 && (
          <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '0.75rem 1rem', marginBottom: '1.5rem', fontSize: '0.8rem', color: '#92400e' }}>
            <i className="bi bi-exclamation-triangle" style={{ marginRight: 6 }} />
            {transitResult.symbolesInconnus.length} symbole(s) non décodé(s)
          </div>
        )}
        <div style={{ display: 'flex', gap: '0.75rem' }}>
          <Link to={`/clients/${clientId}/transits/${transitResult.chart.id}`}
            style={{ padding: '10px 20px', borderRadius: 6, background: '#0891b2', color: '#fff', textDecoration: 'none', fontSize: '0.9rem', fontWeight: 500 }}>
            <i className="bi bi-eye" style={{ marginRight: 6 }} />Consulter les transits
          </Link>
          <Link to={`/clients/${clientId}`}
            style={{ padding: '10px 20px', borderRadius: 6, border: `1px solid ${colors.border}`, background: '#fff', color: colors.text, textDecoration: 'none', fontSize: '0.9rem' }}>
            Retour à la fiche
          </Link>
          <button onClick={() => setTransitResult(null)}
            style={{ padding: '10px 20px', borderRadius: 6, border: `1px solid ${colors.border}`, background: '#fff', color: colors.textMid, fontSize: '0.9rem', cursor: 'pointer', fontFamily: 'inherit' }}>
            Nouvel import
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '1.5rem' }}>
      {/* Formulaire */}
      <div>
        {/* Choix du type */}
        <div style={cardStyle}>
          <h3 style={sectionTitleStyle}>
            <i className="bi bi-list-check" style={{ color: colors.accent, marginRight: 8 }} />
            Type de données
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
            {typeCards.map((t) => (
              <label key={t.key} style={radioCardStyle(dataType === t.key)}>
                <input type="radio" name="dataType" checked={dataType === t.key} onChange={() => { setDataType(t.key); setMessage(null); }} style={{ display: 'none' }} />
                <i className={`bi ${t.icon}`} style={{ fontSize: '1.3rem', color: t.color }} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, color: colors.text, display: 'flex', alignItems: 'center', gap: 6 }}>
                    {t.label}
                    <span style={{
                      fontSize: '0.65rem', padding: '1px 6px', borderRadius: 4,
                      background: t.format === 'PDF' ? '#fef2f2' : '#eff6ff',
                      color: t.format === 'PDF' ? '#dc2626' : '#2563eb',
                      fontWeight: 600,
                    }}>
                      {t.format}
                    </span>
                  </div>
                  <div style={{ fontSize: '0.8rem', color: colors.textMuted }}>{t.desc}</div>
                </div>
              </label>
            ))}
          </div>

        </div>

        {/* Paramètres selon le type */}
        {(needsYear || isTransit) && (
          <div style={{ ...cardStyle, marginTop: '1.5rem' }}>
            <h3 style={sectionTitleStyle}>
              <i className="bi bi-calendar3" style={{ color: colors.accent, marginRight: 8 }} />
              Paramètres
            </h3>

            {dataType === 'revolution_solaire' && (
              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: colors.textMid, marginBottom: 4 }}>
                  Année de la révolution solaire
                </label>
                <input
                  type="number" value={annee} onChange={(e) => setAnnee(parseInt(e.target.value, 10))}
                  min={1900} max={2100}
                  style={{ width: 120, padding: '8px 12px', border: `1px solid ${colors.border}`, borderRadius: 6, fontSize: 14, fontFamily: 'inherit' }}
                />
              </div>
            )}

            {dataType === 'progressions' && (
              <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap' }}>
                <div>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: colors.textMid, marginBottom: 4 }}>
                    Année
                  </label>
                  <input
                    type="number" value={annee} onChange={(e) => setAnnee(parseInt(e.target.value, 10))}
                    min={1900} max={2100}
                    style={{ width: 120, padding: '8px 12px', border: `1px solid ${colors.border}`, borderRadius: 6, fontSize: 14, fontFamily: 'inherit' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: colors.textMid, marginBottom: 4 }}>
                    Date cible des progressions
                  </label>
                  <input
                    type="date" value={progDate} onChange={(e) => {
                      setProgDate(e.target.value);
                      if (e.target.value) {
                        const y = parseInt(e.target.value.split('-')[0]!, 10);
                        if (y >= 1900 && y <= 2100) setAnnee(y);
                      }
                    }}
                    style={{ padding: '8px 12px', border: `1px solid ${colors.border}`, borderRadius: 6, fontSize: 14, fontFamily: 'inherit' }}
                  />
                  <div style={{ fontSize: '0.75rem', color: colors.textMuted, marginTop: 4 }}>
                    Date choisie dans Olympia (ex : 01/01/2026 ou date de consultation)
                  </div>
                </div>
              </div>
            )}

            {isTransit && (
              <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap' }}>
                <div>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: colors.textMid, marginBottom: 4 }}>
                    Date de début
                  </label>
                  <input
                    type="date" value={transitDateDebut} onChange={(e) => setTransitDateDebut(e.target.value)}
                    style={{ padding: '8px 12px', border: `1px solid ${colors.border}`, borderRadius: 6, fontSize: 14, fontFamily: 'inherit' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: colors.textMid, marginBottom: 4 }}>
                    Date de fin
                  </label>
                  <input
                    type="date" value={transitDateFin} onChange={(e) => setTransitDateFin(e.target.value)}
                    style={{ padding: '8px 12px', border: `1px solid ${colors.border}`, borderRadius: 6, fontSize: 14, fontFamily: 'inherit' }}
                  />
                </div>
                <div style={{ fontSize: '0.75rem', color: colors.textMuted, alignSelf: 'flex-end', paddingBottom: 8 }}>
                  Période sélectionnée dans le Traqueur de Dates d'Olympia
                </div>
              </div>
            )}

            {existingForYear && (
              <div style={{
                marginTop: '0.75rem', padding: '8px 12px', borderRadius: 6,
                background: '#fffbeb', border: '1px solid #fde68a',
                fontSize: '0.8rem', color: '#92400e',
              }}>
                <i className="bi bi-exclamation-triangle" style={{ marginRight: 6 }} />
                Une {dataType === 'revolution_solaire' ? 'Révolution Solaire' : 'Progression'} <strong>{annee}</strong> existe déjà pour ce client.
                L'import <strong>remplacera</strong> les données existantes.
              </div>
            )}
          </div>
        )}

        {/* Zone de fichier — CSV ou PDF selon le type */}
        {isTransit ? (
          <div style={{ ...cardStyle, marginTop: '1.5rem' }}>
            <h3 style={sectionTitleStyle}>
              <i className="bi bi-file-earmark-pdf" style={{ color: '#0891b2', marginRight: 8 }} />
              Fichier PDF — Traqueur de Dates
            </h3>
            <div
              onDragOver={(e) => { e.preventDefault(); setTransitDragOver(true); }}
              onDragLeave={() => setTransitDragOver(false)}
              onDrop={(e) => { e.preventDefault(); setTransitDragOver(false); const f = e.dataTransfer.files[0]; if (f) handleTransitFile(f); }}
              onClick={() => !transitUploading && transitInputRef.current?.click()}
              style={{
                border: `2px dashed ${transitDragOver ? '#0891b2' : colors.border}`,
                borderRadius: 10, padding: '2.5rem 2rem', textAlign: 'center',
                cursor: transitUploading ? 'wait' : 'pointer', transition: 'all 0.2s',
                background: transitDragOver ? '#f0f9ff' : colors.bgPage,
                opacity: transitUploading ? 0.6 : 1,
              }}
            >
              <input ref={transitInputRef} type="file" accept=".pdf" onChange={(e) => { const f = e.target.files?.[0]; if (f) handleTransitFile(f); }} style={{ display: 'none' }} disabled={transitUploading} />
              {transitUploading ? (
                <>
                  <i className="bi bi-hourglass-split" style={{ fontSize: '2.5rem', color: '#0891b2', display: 'block', marginBottom: '1rem' }} />
                  <p style={{ fontWeight: 600, marginBottom: '0.5rem' }}>{transitProgress}</p>
                  <div style={{ width: 200, height: 4, background: colors.border, borderRadius: 2, margin: '1rem auto', overflow: 'hidden' }}>
                    <div style={{ height: '100%', background: '#0891b2', borderRadius: 2, animation: 'indeterminate 1.5s infinite', width: '40%' }} />
                  </div>
                </>
              ) : (
                <>
                  <i className="bi bi-file-earmark-pdf" style={{ fontSize: '2.5rem', color: '#0891b2', display: 'block', marginBottom: '1rem' }} />
                  <p style={{ fontWeight: 600, marginBottom: '0.25rem' }}>Glissez votre PDF ici ou cliquez pour sélectionner</p>
                  <p style={{ color: colors.textMuted, fontSize: '0.8rem', margin: 0 }}>PDF du Traqueur de Dates — max 100 MB</p>
                </>
              )}
            </div>
          </div>
        ) : (
          <form onSubmit={handleCsvSubmit}>
            <DropZone
              accept=".csv,text/csv"
              label="Fichier CSV"
              icon="bi-file-earmark-text"
              fileName={csvFileName}
              isDragging={csvDragging}
              onDragOver={() => setCsvDragging(true)}
              onDragLeave={() => setCsvDragging(false)}
              onDrop={(file) => { setCsvDragging(false); readCsvFile(file); }}
              onFileChange={(file) => readCsvFile(file)}
              fileInputRef={csvInputRef}
            />
            <MessageBanner message={message} />
            <FormButtons loading={loading} disabled={!csvContent.trim()} clientId={clientId} loadingText="Import en cours..." />
          </form>
        )}

        {/* Message pour transits */}
        {isTransit && <MessageBanner message={message} />}
      </div>

      {/* Instructions */}
      <div style={{ ...cardStyle, alignSelf: 'start' }}>
        <h3 style={sectionTitleStyle}>
          <i className="bi bi-info-circle" style={{ color: colors.accent, marginRight: 8 }} />
          Instructions Olympia
        </h3>
        <div style={{ fontSize: '0.85rem', color: colors.textMid, lineHeight: 1.7 }}>
          {dataType === 'natal' && (
            <>
              <InstructionStep title="Thème Natal" steps={[
                'Ouvrir Olympia',
                'Sélectionner le client',
                <>Date : <strong>date et heure de naissance exactes</strong></>,
                'Menu Fichier → Exporter → CSV',
              ]} />
              <InfoBox>Un seul thème natal par client. Un nouvel import remplacera le précédent.</InfoBox>
            </>
          )}
          {dataType === 'revolution_solaire' && (
            <>
              <InstructionStep title="Révolution Solaire" steps={[
                'Ouvrir Olympia → sélectionner le client',
                <>Menu <strong>Thème → Révolution Solaire</strong></>,
                <>Sélectionner la <strong>date anniversaire de l'année souhaitée</strong> (ex : anniversaire 2026)</>,
                'Olympia calcule la RS automatiquement',
                'Menu Fichier → Exporter → CSV',
              ]} />
              <InfoBox>La date correspond au retour exact du Soleil sur sa position natale. On stocke une RS par année — un nouvel import pour la même année remplacera le précédent.</InfoBox>
            </>
          )}
          {dataType === 'progressions' && (
            <>
              <InstructionStep title="Progressions (Directions secondaires)" steps={[
                'Ouvrir Olympia → sélectionner le client',
                <>Menu <strong>Thème → Directions secondaires</strong></>,
                <>Date cible : <strong>1er janvier de l'année en cours</strong> ou la <strong>date de consultation</strong></>,
                'Principe : 1 jour après la naissance = 1 an de vie',
                'Menu Fichier → Exporter → CSV',
              ]} />
              <InfoBox>Ex : pour 2026, une personne née le 22/04/1981 → Olympia calcule les positions au 22/04/1981 + 45 jours (06/06/1981).</InfoBox>
            </>
          )}
          {dataType === 'transits' && (
            <>
              <InstructionStep title="Transits — Traqueur de Dates" steps={[
                'Ouvrir Olympia → sélectionner le client',
                <>Menu <strong>Transit → Traqueur de Dates</strong></>,
                <>Définir la <strong>période</strong> : date de début → date de fin (ex : 01/01/2026 → 31/12/2026)</>,
                <>Sélectionner les <strong>planètes transitantes</strong> (Jupiter, Saturne, Uranus, Neptune, Pluton = les plus significatives)</>,
                <>Cocher les <strong>types d'aspects</strong> (Conjonction, Opposition, Trigone, Carré, Sextile)</>,
                <>Cliquer sur <strong>Calculer</strong></>,
                <>Exporter en <strong>PDF</strong></>,
              ]} />
              <InfoBox>Pour une consultation, sélectionnez l'<strong>année en cours</strong>. Le parsing peut prendre plusieurs minutes pour les fichiers volumineux.</InfoBox>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Section Documents PDF ───

function DocumentImportSection({ clientId }: { clientId: string }) {
  const { reloadClient } = useClient();
  const [docType, setDocType] = useState('theme_natal_roue');
  const [customName, setCustomName] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFile = useCallback((f: File) => {
    if (!f.type.includes('pdf') && !f.name.toLowerCase().endsWith('.pdf')) {
      setMessage({ type: 'error', text: 'Veuillez sélectionner un fichier PDF' });
      return;
    }
    setFile(f);
    setMessage(null);
  }, []);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!file) return;
    setLoading(true);
    setMessage(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('type', docType);
      if (customName.trim()) formData.append('nomPersonnalise', customName.trim());
      const res = await apiUpload<{ fichier: { id: number } }>(`/clients/${clientId}/files`, formData);
      if (res.success) {
        setMessage({ type: 'success', text: 'Document uploadé !' });
        setFile(null);
        setCustomName('');
        await reloadClient();
      } else {
        setMessage({ type: 'error', text: res.error ?? "Erreur lors de l'upload" });
      }
    } catch {
      setMessage({ type: 'error', text: 'Erreur de connexion au serveur' });
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '1.5rem' }}>
      <form onSubmit={handleSubmit}>
        {/* Type de document */}
        <div style={cardStyle}>
          <h3 style={sectionTitleStyle}>
            <i className="bi bi-list-check" style={{ color: colors.accent, marginRight: 8 }} />
            Type de document
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
            {DOC_TYPES.map((dt) => (
              <label key={dt.key} style={radioCardStyle(docType === dt.key)}>
                <input type="radio" name="docType" checked={docType === dt.key} onChange={() => setDocType(dt.key)} style={{ display: 'none' }} />
                <i className={`bi ${dt.icon}`} style={{ fontSize: '1.3rem', color: dt.color }} />
                <span style={{ fontWeight: 500, fontSize: '0.9rem', color: colors.text }}>{dt.label}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Nom personnalisé */}
        <div style={{ ...cardStyle, marginTop: '1.5rem' }}>
          <h3 style={{ ...sectionTitleStyle, marginBottom: '0.75rem' }}>
            <i className="bi bi-tag" style={{ color: colors.accent, marginRight: 8 }} />
            Nom personnalisé <span style={{ fontWeight: 400, fontSize: '0.85rem', color: colors.textMuted }}>(optionnel)</span>
          </h3>
          <input
            type="text" value={customName} onChange={(e) => setCustomName(e.target.value)}
            placeholder="Laisser vide pour utiliser le nom par défaut"
            style={{ width: '100%', boxSizing: 'border-box', padding: '10px 14px', border: `1px solid ${colors.border}`, borderRadius: 6, fontSize: 14, fontFamily: 'inherit', color: colors.text }}
          />
        </div>

        {/* Zone de drop PDF */}
        <div style={{ marginTop: '1.5rem' }}>
          <DropZone
            accept=".pdf,application/pdf"
            label="Fichier PDF"
            icon="bi-file-pdf"
            fileName={file?.name ?? ''}
            isDragging={isDragging}
            onDragOver={() => setIsDragging(true)}
            onDragLeave={() => setIsDragging(false)}
            onDrop={(f) => { setIsDragging(false); handleFile(f); }}
            onFileChange={(f) => handleFile(f)}
            fileInputRef={fileInputRef}
          />
        </div>

        <MessageBanner message={message} />
        <FormButtons loading={loading} disabled={!file} clientId={clientId} loadingText="Upload en cours..." submitLabel="Envoyer" />
      </form>

      {/* Instructions */}
      <div style={{ ...cardStyle, alignSelf: 'start' }}>
        <h3 style={sectionTitleStyle}>
          <i className="bi bi-info-circle" style={{ color: colors.accent, marginRight: 8 }} />
          Instructions Olympia
        </h3>
        <div style={{ fontSize: '0.85rem', color: colors.textMid, lineHeight: 1.7 }}>
          <InstructionStep title="Documents PDF" steps={[
            'Ouvrir le thème du client dans Olympia',
            <>Pour la <strong>Roue</strong> : Affichage → Roue → Imprimer en PDF</>,
            <>Pour le <strong>Tableau Planètes</strong> : Affichage → Tableau des planètes → Exporter PDF</>,
            <>Pour le <strong>Tableau Aspects</strong> : Affichage → Grille des aspects → Exporter PDF</>,
            <>Pour le <strong>Listing Aspects</strong> : Affichage → Listing des aspects → Exporter PDF</>,
            <>Pour les <strong>Dignités</strong> : Affichage → Tableau des dignités → Exporter PDF</>,
          ]} />
          <InfoBox>Ces documents sont des visuels de référence stockés dans le dossier du client. Ils ne sont pas analysés automatiquement.</InfoBox>
        </div>
      </div>
    </div>
  );
}

// ─── Composants partagés ───

function DropZone({ accept, label, icon, fileName, isDragging, onDragOver, onDragLeave, onDrop, onFileChange, fileInputRef }: {
  accept: string; label: string; icon: string; fileName: string; isDragging: boolean;
  onDragOver: () => void; onDragLeave: () => void; onDrop: (file: File) => void;
  onFileChange: (file: File) => void; fileInputRef: React.RefObject<HTMLInputElement>;
}) {
  return (
    <div style={{ ...cardStyle, marginTop: '1.5rem' }}>
      <h3 style={sectionTitleStyle}>
        <i className={`bi ${icon}`} style={{ color: colors.accent, marginRight: 8 }} />
        {label}
      </h3>
      <div
        onClick={() => fileInputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); onDragOver(); }}
        onDragLeave={(e) => { e.preventDefault(); onDragLeave(); }}
        onDrop={(e) => { e.preventDefault(); const f = e.dataTransfer.files[0]; if (f) onDrop(f); }}
        style={{
          border: `2px dashed ${isDragging ? colors.accent : colors.border}`,
          borderRadius: 10, padding: '2rem', textAlign: 'center',
          cursor: 'pointer', transition: 'all 0.2s',
          background: isDragging ? `${colors.accent}0d` : colors.bgPage,
        }}
      >
        <i className="bi bi-cloud-arrow-up" style={{ fontSize: '2.5rem', color: isDragging ? colors.accent : colors.textMuted, display: 'block', marginBottom: '0.75rem' }} />
        {fileName ? (
          <p style={{ margin: 0, color: colors.text, fontWeight: 500 }}>
            <i className="bi bi-file-earmark-check" style={{ color: '#16a34a', marginRight: 6 }} />{fileName}
          </p>
        ) : (
          <>
            <p style={{ margin: '0 0 0.25rem', color: colors.text, fontWeight: 500 }}>Glissez votre fichier ici</p>
            <p style={{ margin: 0, fontSize: '0.85rem', color: colors.textMuted }}>ou cliquez pour sélectionner</p>
          </>
        )}
        <input ref={fileInputRef} type="file" accept={accept} onChange={(e) => { const f = e.target.files?.[0]; if (f) onFileChange(f); }} style={{ display: 'none' }} />
      </div>
    </div>
  );
}

function InstructionStep({ title, steps }: { title: string; steps: React.ReactNode[] }) {
  return (
    <>
      <h4 style={{ margin: '0 0 0.5rem', fontSize: '0.9rem', color: colors.text }}>{title}</h4>
      <ol style={{ margin: '0 0 1rem', paddingLeft: '1.2rem' }}>
        {steps.map((step, i) => <li key={i} style={{ marginBottom: 4 }}>{step}</li>)}
      </ol>
    </>
  );
}

function InfoBox({ children }: { children: React.ReactNode }) {
  return (
    <div style={{
      background: `${colors.accent}0d`, border: `1px solid ${colors.accent}30`,
      borderRadius: 8, padding: '0.75rem 1rem', fontSize: '0.8rem',
      color: colors.textMid, lineHeight: 1.6,
    }}>
      <i className="bi bi-lightbulb" style={{ color: colors.accent, marginRight: 6 }} />
      {children}
    </div>
  );
}

function MessageBanner({ message }: { message: { type: 'success' | 'error'; text: string } | null }) {
  if (!message) return null;
  return (
    <div style={{
      padding: '10px 14px', borderRadius: 8, marginTop: '1rem',
      background: message.type === 'success' ? '#dcfce7' : '#fef2f2',
      color: message.type === 'success' ? '#166534' : '#991b1b',
      border: `1px solid ${message.type === 'success' ? '#86efac' : '#fca5a5'}`,
      fontSize: 14,
    }}>
      <i className={`bi ${message.type === 'success' ? 'bi-check-circle' : 'bi-exclamation-circle'}`} style={{ marginRight: 6 }} />
      {message.text}
    </div>
  );
}

function FormButtons({ loading, disabled, clientId, loadingText = 'En cours...', submitLabel = 'Importer' }: {
  loading: boolean; disabled: boolean; clientId: string; loadingText?: string; submitLabel?: string;
}) {
  return (
    <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1rem' }}>
      <button type="submit" disabled={loading || disabled} style={{
        padding: '10px 24px', borderRadius: 6, border: 'none',
        background: loading || disabled ? '#e5e5e5' : colors.accent,
        color: loading || disabled ? colors.textMuted : colors.text,
        fontSize: 14, fontWeight: 600, cursor: loading || disabled ? 'not-allowed' : 'pointer', fontFamily: 'inherit',
      }}>
        {loading ? loadingText : submitLabel}
      </button>
      <Link to={`/clients/${clientId}`} style={{
        padding: '10px 24px', borderRadius: 6, border: `1px solid ${colors.border}`,
        background: '#fff', color: colors.textMid, fontSize: 14, textDecoration: 'none',
        fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center',
      }}>
        Annuler
      </Link>
    </div>
  );
}

// ─── Styles partagés ───

const cardStyle: React.CSSProperties = {
  background: '#fff', borderRadius: 10, padding: '1.5rem',
  boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
};

const sectionTitleStyle: React.CSSProperties = {
  margin: '0 0 1rem', fontSize: '1rem', color: colors.text,
};

function radioCardStyle(selected: boolean): React.CSSProperties {
  return {
    display: 'flex', alignItems: 'center', gap: 12,
    padding: '0.75rem 1rem', borderRadius: 8, cursor: 'pointer',
    border: `2px solid ${selected ? colors.accent : colors.border}`,
    background: selected ? `${colors.accent}0d` : '#fff',
    transition: 'all 0.2s',
  };
}
